import React, { useCallback, useEffect, useState } from "react";

import useVideoControls from "@app/analytics/hooks/useVideoControls";
import type { VideoClip, VideoPlayerAction } from "@app/analytics/types";

interface UseVideoClipOptions {
  autoPlay?: boolean;
  maximizeOnPlay?: "always" | "on_start" | "never";
  pauseOnMinimize?: boolean;
  onPlay?: (id: VideoClip["id"]) => void;
  onPlayed?: (id: VideoClip["id"]) => void;
  onMinimize?: () => void;
}

const useVideoClip = (
  video: HTMLVideoElement | null,
  clip: VideoClip | null,
  opts?: UseVideoClipOptions
) => {
  const [played, setPlayed] = useState(false);
  const controls = useVideoControls(video, {
    autoPlay: opts?.autoPlay,
    pauseOnMinimize: opts?.pauseOnMinimize,
  });

  useEffect(() => {
    // reset the state when the clip id changes
    setPlayed(false);
    controls.reset();
  }, [clip?.id]);

  const handleTimeUpdate = useCallback(() => {
    if (!video || !clip) return;

    controls.updateTime(video.currentTime);

    if (video.currentTime >= clip.end) {
      controls.pause();
      setPlayed(true);
      opts?.onPlayed?.(clip.id);
    }
  }, [video, clip?.id]);

  const play = () => {
    if (!video || !clip) return;

    const playTimestamp =
      video.currentTime < clip.start || played ? clip.start : undefined;

    return controls.play(playTimestamp, () => {
      opts?.onPlay?.(clip.id);
      setPlayed(false);
    });
  };

  const handleScrubberChange = (timestamp: number) => {
    if (!clip) return;

    controls.jumpToTimestamp(clip.start + timestamp);
  };

  const handleVideoClick: React.MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!video || !clip) return;

    video.paused ? play() : controls.pause();

    const shouldMaximize =
      opts?.maximizeOnPlay === "always" ||
      (opts?.maximizeOnPlay === "on_start" && video.currentTime <= clip.start);

    if (shouldMaximize) controls.maximize();
  };

  const handleActionClick = (type: VideoPlayerAction) => {
    switch (type) {
      case "minimize":
        return opts?.onMinimize ? opts.onMinimize() : controls.minimize();
      case "maximize":
        return controls.maximize();
      case "play":
        return play();
      case "pause":
        return controls.pause();
      case "mute":
        return controls.mute();
      case "unmute":
        return controls.unmute();
      default:
        return;
    }
  };

  useEffect(() => {
    if (!video) return;

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [handleTimeUpdate]);

  const currentTime = clip
    ? Math.min(Math.max(controls.currentTime - clip.start, 0), clip.end)
    : 0;

  return {
    controls,
    currentTime,
    onActionClick: handleActionClick,
    onScrubberChange: handleScrubberChange,
    onVideoClick: handleVideoClick,
    played,
  };
};

export default useVideoClip;
